import React from 'react';

import Page from './Page';

import { RouteComponentProps, Redirect } from 'react-router-dom';
import { logIn, logInWithRecoveryToken } from '../lib/api/user';

import { parseLoggedUserToken, storeUserToken } from '../lib/auth';

import './Login.scss';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import {
  BubbleButton,
  BubbleAlert,
  BubbleFormGroup,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';
import { readQs } from '@go-shared/lib/querystring';
import { PublicSSOInfo } from '@go-shared/types/info';

export type LoginPageProps = RouteComponentProps<any, any, any>;

export interface LoginState {
  mode: 'login' | 'forgot';
  logInSuccess: boolean;
  logInError?: string;
  username: string;
  password: string;
  confirmPassword: string;
  recoveryToken: string;
}

const defaultState: LoginState = {
  mode: 'login',
  logInSuccess: false,
  username: '',
  password: '',
  confirmPassword: '',
  recoveryToken: '',
};

class LoginClass extends React.Component<
  LoginPageProps & { brandLogo: string; sso?: PublicSSOInfo; showBasicLogin: boolean },
  LoginState
> {
  public static contextType = FeedEditorCtx;
  public readonly state: LoginState = defaultState;
  public context!: React.ContextType<typeof FeedEditorCtx>;

  public render() {
    let tokenQs = readQs().token;
    if (tokenQs && typeof tokenQs === 'string') {
      storeUserToken(tokenQs);
      return <Redirect to="/" />;
    }

    if (this.state.logInSuccess) {
      try {
        parseLoggedUserToken();
      } catch (e) {
        console.error(e);
        this.setState({
          logInSuccess: false,
        });

        return;
      }

      const referrerPath =
        this.props.location && this.props.location.state && this.props.location.state.from
          ? this.props.location.state.from
          : '/';

      return <Redirect to={referrerPath} />;
    }

    const { sso, showBasicLogin: showLogin } = this.props;

    const showSSOLogin = !!sso;

    return (
      <Page className="LoginPage">
        <div className="h-full grid items-center justify-center">
          <div className="shadow-xl" style={{ width: '400px' }}>
            <div className="aspect-wide bg-ocean logo">
              <div
                className="brand-logo"
                style={{ backgroundImage: `url("${this.props.brandLogo}")` }}
              />
            </div>
            {this.state.mode === 'forgot' && (
              <BubbleAlert variant="banana" className="rounded-0-i">
                <span>You will need a recovery key to reset your password</span>
              </BubbleAlert>
            )}
            {this.state.logInError && (
              <BubbleAlert variant="crimson" className="rounded-0-i">
                <span>{this.state.logInError}</span>
              </BubbleAlert>
            )}
            <div className="p-8 bg-white relative">
              {showLogin && (
                <form onSubmit={this.handleLogInClick.bind(this)}>
                  <div className="grid grid-cols-1 gap-6">
                    <BubbleFormGroup label="Username">
                      <BubbleInput
                        type="text"
                        block
                        autoFocus
                        autoComplete="username"
                        value={this.state.username}
                        onChange={this.handleUsernameChange.bind(this)}
                      />
                    </BubbleFormGroup>

                    <BubbleFormGroup
                      label={this.state.mode === 'login' ? 'Password' : 'New Password'}
                    >
                      <BubbleInput
                        type="password"
                        block
                        autoComplete={
                          this.state.mode === 'login' ? 'current-password' : 'new-password'
                        }
                        value={this.state.password}
                        onChange={this.handlePasswordChange.bind(this)}
                      />
                    </BubbleFormGroup>

                    {this.state.mode === 'forgot' && (
                      <>
                        <BubbleFormGroup label="Confirm Password">
                          <BubbleInput
                            type="password"
                            block
                            autoComplete="new-password"
                            value={this.state.confirmPassword}
                            onChange={this.handleConfirmPasswordChange.bind(this)}
                          />
                        </BubbleFormGroup>
                        <BubbleFormGroup label="Recovery Key">
                          <BubbleInput
                            type="password"
                            block
                            value={this.state.recoveryToken}
                            onChange={this.handleRecoveryTokenChange.bind(this)}
                          />
                        </BubbleFormGroup>
                      </>
                    )}
                    <div>
                      <BubbleButton
                        className="mt-2"
                        block
                        type="submit"
                        variant={this.state.mode === 'login' ? 'ocean' : 'crimson'}
                      >
                        {this.state.mode === 'login' ? 'Login' : 'Reset Password'}
                      </BubbleButton>
                      <BubbleButton
                        className="mt-1"
                        type="button"
                        inverse
                        block
                        variant="navy"
                        onClick={this.handleOnSwapModes.bind(this)}
                      >
                        {this.state.mode === 'forgot' ? 'Return to Login' : 'Reset Password'}
                      </BubbleButton>
                    </div>
                  </div>
                </form>
              )}
              {showSSOLogin && (
                <>
                  {showLogin && <hr className="my-5" />}
                  <div className="text-center">
                    <BubbleButton variant="okta" onClick={() => (location.href = sso.url)}>
                      Log In with <b>{sso.name.toUpperCase()}</b>
                    </BubbleButton>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  private handleUsernameChange(event: React.ChangeEvent<InputElement>) {
    this.setState({ username: event.target.value });
  }

  private handlePasswordChange(event: React.ChangeEvent<InputElement>) {
    this.setState({ password: event.target.value });
  }

  private handleConfirmPasswordChange(event: React.ChangeEvent<InputElement>) {
    this.setState({ confirmPassword: event.target.value });
  }

  private handleRecoveryTokenChange(event: React.ChangeEvent<InputElement>) {
    this.setState({ recoveryToken: event.target.value });
  }

  private async handleLogInClick(evt: React.SyntheticEvent) {
    evt.preventDefault();

    if (this.state.mode === 'forgot' && this.state.password !== this.state.confirmPassword) {
      this.setState({ logInError: 'Passwords do not match', logInSuccess: false });
      return;
    }

    try {
      let user =
        this.state.mode === 'login'
          ? await logIn(this.state.username, this.state.password)
          : await logInWithRecoveryToken(
              this.state.username,
              this.state.password,
              this.state.recoveryToken
            );
      storeUserToken(user.token);
      this.context.setEditMode(user.feedEditor);
      this.setState({ logInError: undefined, logInSuccess: true });
    } catch (e) {
      this.setState({ logInError: 'The data you entered is not valid', logInSuccess: false });
      console.error(e);
    }
  }

  private async handleOnSwapModes() {
    if (this.state.mode === 'forgot') {
      this.setState({ mode: 'login', logInError: undefined });
    } else {
      this.setState({ mode: 'forgot', logInError: undefined });
    }
  }
}

export default function Login(props: LoginPageProps) {
  const { brandLogo, sso, showBasicLogin: showLogin } = usePublicServerInfo();

  return <LoginClass {...props} brandLogo={brandLogo} sso={sso} showBasicLogin={showLogin} />;
}
